import * as Clerk from "@clerk/remix";
import * as Radix from "@radix-ui/themes";
import { never, Vivid } from "@vendrinc/frontend-ts";
import * as React from "react";
import styles from "./ClerkAuthentication.module.css";
import * as Image from "~/image";
import * as paths from "~/paths";

export type CompanyType = {
  id: string;
  name: string;
  icon?: string;
  stats: {
    saveUpTo?: number;
  };
};

export type ClerkAuthenticationMode =
  | {
      type: "default";
    }
  | {
      type: "privateOfferPurchaseScope";
    }
  | {
      type: "priceEstimate";
      company: CompanyType;
    };

export type ClerkAuthenticationProps = {
  mode: ClerkAuthenticationMode;
  redirectUrl?: string | null;
};

export const ClerkAuthentication = ({
  mode,
  redirectUrl,
}: ClerkAuthenticationProps) => {
  const forceRedirectUrl = redirectUrl ?? paths.home();

  switch (mode.type) {
    case "default":
      return (
        <Radix.Flex
          direction="column"
          gap="7"
          justify="center"
          align="center"
          width="400px"
        >
          <ClerkSignIn forceRedirectUrl={forceRedirectUrl} />
          <TermsAndPrivacyPolicy />
        </Radix.Flex>
      );

    case "privateOfferPurchaseScope":
      return (
        <Radix.Flex
          direction="column"
          gap="5"
          justify="center"
          align="center"
          width="400px"
        >
          <Vivid.Heading
            node="h1"
            align="left"
            style={{ fontSize: "24px", fontWeight: "700", lineHeight: "1.1" }}
          >
            Enter your email to continue and unlock your pricing
          </Vivid.Heading>
          <ClerkSignIn forceRedirectUrl={forceRedirectUrl} />
          <TermsAndPrivacyPolicy />
        </Radix.Flex>
      );

    case "priceEstimate":
      return (
        <Radix.Flex
          direction="column"
          gap="5"
          justify="center"
          align="center"
          width="430px"
        >
          <Radix.Flex direction="column" gap="16px" mb="16px">
            <Radix.Flex
              direction="row"
              gap="16px"
              justify="center"
              align="center"
            >
              <img
                src={mode.company.icon}
                alt={mode.company.name}
                width="56px"
              />
              <img
                src={Image.lockUnlocked}
                alt="Lock unlocked"
                width="32px"
                className={styles.priceEstimateLock}
              />
            </Radix.Flex>
            <Vivid.Heading
              node="h2"
              align="center"
              className={styles.priceEstimateHeading}
            >
              Unlock your custom price estimate for {mode.company.name}{" "}
              instantly
            </Vivid.Heading>
            <Vivid.Text
              align="center"
              size="14px"
              weight="400"
              color="nextGreyscale800"
            >
              We&apos;ve helped 7,659 buyers with similar pricing requests
            </Vivid.Text>
            <PriceEstimateBenefits company={mode.company} />
          </Radix.Flex>
          <Radix.Flex
            direction="column"
            gap="5"
            justify="center"
            align="center"
            width="400px"
          >
            <ClerkSignIn forceRedirectUrl={forceRedirectUrl} />
            <TermsAndPrivacyPolicy />
          </Radix.Flex>
        </Radix.Flex>
      );

    default:
      return never(mode);
  }
};

type ClerkSignInProps = {
  forceRedirectUrl: string;
};

const ClerkSignIn = ({ forceRedirectUrl }: ClerkSignInProps) => {
  return (
    <Clerk.SignIn
      appearance={{
        elements: {
          input: styles.input,
          formButtonPrimary: styles.button,
          cardBox: styles.card,
          card: styles.card,
          headerTitle: styles.headerTitle,
          footer: styles.footer,
        },
      }}
      forceRedirectUrl={forceRedirectUrl}
      // @ts-expect-error Clerk's SignIn component supports fallback prop but it's missing from types
      fallback={<SignInSkeleton />}
    />
  );
};

const PriceEstimateBenefits = ({ company }: { company: CompanyType }) => {
  const benefits = React.useMemo(
    () => [
      ...(company.stats.saveUpTo
        ? [
            `Learn the fair price for ${company.name} and save up to ${Math.round(company.stats.saveUpTo)}% on software`,
          ]
        : []),
      "View and compare pricing for competitors",
      "Get access to unlimited pricing benchmarks",
    ],
    [company.name, company.stats],
  );

  return (
    <Radix.Flex
      direction="column"
      gap="24px"
      className={styles.priceEstimateBenefits}
    >
      {benefits.map((benefit, index) => (
        <PriceEstimateBenefitItem key={index} benefit={benefit} />
      ))}
    </Radix.Flex>
  );
};

const PriceEstimateBenefitItem = ({ benefit }: { benefit: string }) => {
  return (
    <Radix.Flex direction="row" gap="8px" align="center">
      <span className={styles.priceEstimateBenefitCheck}>
        {Vivid.Icon.check}
      </span>
      <Vivid.Text weight="400" size="12px" color="nextGreyscale800">
        {benefit}
      </Vivid.Text>
    </Radix.Flex>
  );
};

const SignInSkeleton = () => {
  return <Radix.Skeleton className={styles.signInSkeleton} />;
};

const TermsAndPrivacyPolicy = () => {
  return (
    <Vivid.Text className={styles.termsAndPrivacyPolicy}>
      Unless subject to a separate agreement with Vendr, by signing up, you
      agree to our{" "}
      <a href="https://www.vendr.com/legal/plus-msa">
        Master Service Agreement
      </a>{" "}
      and have both read and acknowledged our{" "}
      <a href="https://www.vendr.com/legal/privacy-policy">Privacy Policy</a>.
    </Vivid.Text>
  );
};

export const ClerkSignUp = () => {
  // Generate a one-time nonce for the signup flow
  const nonce = React.useMemo(() => {
    return btoa(Date.now().toString() + Math.random().toString());
  }, []);

  return (
    <Clerk.SignUp
      appearance={{
        elements: {
          input: styles.input,
          formButtonPrimary: styles.button,
          cardBox: styles.card,
          card: styles.card,
          headerTitle: styles.headerTitle,
          footer: styles.footer,
        },
      }}
      // @ts-expect-error Clerk's SignIn component supports fallback prop but it's missing from types
      fallback={<SignUpSkeleton />}
      forceRedirectUrl={paths.signupPostSetup(nonce)}
    />
  );
};

const SignUpSkeleton = () => {
  return <Radix.Skeleton width="400px" height="450px" />;
};

export const ClerkOrganizationSelector = ({
  redirectUrl,
}: {
  redirectUrl: string;
}) => {
  return (
    <Clerk.OrganizationList
      appearance={{
        elements: {
          input: styles.input,
          formButtonPrimary: styles.button,
          cardBox: styles.card,
          card: styles.card,
          headerTitle: styles.headerTitle,
          footer: styles.footer,
        },
      }}
      afterCreateOrganizationUrl={redirectUrl}
      afterSelectOrganizationUrl={redirectUrl}
      hideSlug
      hidePersonal
      fallback={<OrganizationListSkeleton />}
    />
  );
};

const OrganizationListSkeleton = () => {
  return <Radix.Skeleton width="400px" height="375px" />;
};
